import moment from "moment";
import { AdvancedReorderRuleModel } from "src/app/_models/advanced-reorder-rule.model";
import { AdvancedReorderHelper } from "./advanced-reorder-helper";
import { AdvancedReorderConfig } from "./advanced-reorder-config";
import { environment } from "src/environments/environment";
import { DatatableData } from "src/app/_models/datatable-data";
import { RangePickerComponent } from "../theme/range-picker/range-picker.component";
import { Component, ElementRef, ViewChild, inject } from "@angular/core";
import { FilterOption } from "../theme/filters/filters.component";
import { Tags } from "src/app/_global/tags";
import { AdvancedReorderSettingsComponent } from "./settings/advanced-reorder-settings.component";
import { AdvancedReorderItem } from "./advanced-reorder-item";
import { EditAdvancedReorderRuleComponent } from "../settings/advanced-reorder-rules/edit/edit-advanced-reorder-rule.component";
import { AdvancedReordersModel } from "src/app/_models/advanced-reorders-model";
import { AdvancedReorderModel } from "src/app/_models/advanced-reorder.model";

@Component({ template: '' })
export class AdvancedReorderPage extends AdvancedReorderHelper {
  public tags: Tags = inject(Tags);
  data: DatatableData = new DatatableData();
  allFilters: any = {};
  adjSign: string = '+';
  headerExpanded: boolean = false;
  imageUrl: string = environment.baseUrl + "/products/image/";
  selectedFilters: any[] = [];
  selectedTags: number[] = [];
  tagsChanged: boolean = false;
  orderFor: RangePickerComponent;
  @ViewChild('orderFor1') orderFor1: RangePickerComponent;
  @ViewChild('orderFor2') orderFor2: RangePickerComponent;
  basedOn: RangePickerComponent;
  @ViewChild('basedOn1') basedOn1: RangePickerComponent;
  @ViewChild('basedOn2') basedOn2: RangePickerComponent;
  @ViewChild('searchBox') searchBox: ElementRef;
  math = Math;
  filterOptions: FilterOption[] = [
    { title: 'Reorder', field: 'reorder', inputType: 'input', value: 0, type: 'Equal', sort: 'desc' },
    { title: 'Orders', field: 'orders', inputType: 'input', value: 0, type: 'Equal', sort: 'desc' },
    { title: 'Profit', field: 'profit', inputType: 'input', value: 0, type: 'Equal', sort: 'desc' },
    { title: 'Inventory', field: 'totalInventory', inputType: 'input', value: 0, type: 'Equal', sort: 'desc' },
    { title: 'Supplier', field: 'supplier', inputType: '[]', value: '', type: 'StringEqual', sort: 'asc' }
  ];

  constructor() {
      super();
      this.data.pageSize = 10;
      if (['AdvancedReorderComponent', 'AdvancedReorderAllListingsComponent'].includes(this.constructor.name))
        this.filterOptions.push({ title: 'Supplier', field: 'supplier', inputType: '[]', value: '', type: 'StringEqual', sort: 'desc' });
  }

  toggleHeader() {
    let oldBasedOn = this.basedOn;
    let oldOrderFor = this.orderFor;
    this.headerExpanded = !this.headerExpanded;
    setTimeout(() => {
      if (this.headerExpanded) {
        this.basedOn = this.basedOn2;
        this.orderFor = this.orderFor2;
      } else {
        this.basedOn = this.basedOn1;
        this.orderFor = this.orderFor1;
      }
      this.basedOn.setData(oldBasedOn);
      this.orderFor.setData(oldOrderFor);
    }, 0);
  }

  filterChanged() {
    delete this.selectedRule;
    this.ruleChanged = true;
  }

  applyFilters(e: FilterOption[]) {
    this.selectedFilters = e;
    if (e.length || Object.keys(this.data.filters).length) {
      e.forEach(filter => this.data.filters[filter.field] = { type: filter.type, value: filter.value });
      this.loadData();
    }
  }

  removedFilter(e: FilterOption) {
    delete this.data.filters[e.field];
    this.loadData();
  }

  openSettings(item: any = this) {
    let modal = this.modalService.open(AdvancedReorderSettingsComponent);
    modal.componentInstance.item = item;
  }

  cloneData(data: DatatableData) {
    let newData = { ...data };
    newData.items = newData.items.map(item => this.cloneItem(item));
    return newData;
  }
  
  get sortTitle() {
    return this.data.sortField
      ? this.filterOptions.find(
          (o) => o.field.toLowerCase() === this.data.sortField.toLowerCase()
        ).title
      : null;
  }

  sortByDirection() {
    this.data.sort = this.data.sort === 'asc' ? 'desc' : 'asc';
    this.loadData();
  }

  changeSort(option: FilterOption) {
    this.data.sort = option.sort;
    this.data.sortField = option.field;
    if (this.selectedFilters.length) {
      this.selectedFilters.forEach((filter) => {
        this.data.filters[filter.field] = {
          type: filter.type,
          value: filter.value,
        };
      });
    }
    this.loadData();
  }

  truncate(content: string, chars: number = 30) {
    if (!content) return content;
    if (content.length > chars) {
      return content.slice(0, chars) + '...';
    } else {
      return content;
    }
  }

  removeAll() {
    this.loadingService.loading = true;
    this.advReorderService.removeAllOrders().subscribe(() => {
      this.addedReorders = [];
      for (let item of this.data.items) {
        delete item.data.addedQuantity;
      }
      this.loadingService.loading = false;
    }, err => this.loadingService.loading = false);
  }

  addNewRule(rule: AdvancedReorderRuleModel = new AdvancedReorderRuleModel()) {
    let modal = this.modalService.open(EditAdvancedReorderRuleComponent, { size: 'lg' });
    modal.componentInstance.reorderRule = rule;
    modal.result.then(() => {
      this.loadData();
      this.advReorderRules.rules.push(rule);
    });
  }
  
  selectRule(rule: AdvancedReorderRuleModel) {
    if (rule) {
      this.ruleChanged = true;
      this.basedOn.selectedDays = rule.basedOn;
      this.basedOn.hasCustom = false;
      this.basedOn.selectedMonths = [];
      if (rule.basedOnMonths) {
        this.basedOn.hasCustom = true;
        for(let month of rule.basedOnMonths.replace(/{|}/g, '').split(',')) {
          let date = moment(month);
          this.basedOn.selectMonth(date.month(), date.year() - 2000);
        }
      }
      this.orderFor.selectedDays = rule.orderFor;
      this.orderFor.hasCustom = false;
      this.orderFor.selectedMonths = [];
      if (rule.orderForMonths) {
        this.orderFor.hasCustom = true;
        for (let month of rule.orderForMonths.replace(/{|}/g, '').split(',')) {
          let date = moment(month);
          this.orderFor.selectMonth(date.month(), date.year() - 2000);
        }
      }
      this.formGroup.controls.minusInventory.setValue(false);
      this.formGroup.controls.minusStoreInventory.setValue(false);
      this.formGroup.controls.minusOverstockedStoreInventory.setValue(false);
      this.formGroup.controls.minusInventory.setValue(rule.minusInventory == 'true');
      this.formGroup.controls.minusStoreInventory.setValue(rule.minusStoreInventory == 'true');
      this.formGroup.controls.minusOverstockedStoreInventory.setValue(rule.minusOverstockedStoreInventory == 'true');
      this.locations.getAll(locations => {
        this.stores.getAll(stores => {
          if (rule.minusInventory && rule.minusInventory != 'true') {
            let locationsIds: number[] = rule.minusInventory.split(',').map(id => Number(id));
            for (let location of locations)
              this.locationsFormGroup.controls[location.id].setValue(locationsIds.includes(location.id));
          }
          if (rule.minusStoreInventory && rule.minusStoreInventory != 'true') {
            let storesIds: number[] = rule.minusStoreInventory.split(',').map(id => Number(id));
            for (let store of stores)
              this.storesFormGroup.controls[store.id].setValue(storesIds.includes(store.id));
          }
          if (rule.minusOverstockedStoreInventory && rule.minusOverstockedStoreInventory != 'true') {
            let overstockStoresIds: number[] = rule.minusOverstockedStoreInventory.split(',').map(id => Number(id));
            for (let store of stores)
              this.overstockedStoresFormGroup.controls[store.id].setValue(overstockStoresIds.includes(store.id));
          }
        });
      });
      this.formGroup.controls.minusPOs.setValue(rule.minusOpenPurchases);
      this.formGroup.controls.outOfStock.setValue(rule.outOfStock);
      this.formGroup.controls.buyBox.setValue(rule.buyBox);
      this.formGroup.controls.adjustment.setValue(Math.abs(rule.adjustment));
      this.adjSign = rule.adjustment < 0 ? '-' : '+';
      this.selectedRule = rule;
    }
  }

  private getAllTagIds(): number[] {
    const dynamic = (this.tags.getAll() || [])
      .map((t: any) => Number(t?.tagId ?? t?.id))
      .filter((v: number) => Number.isFinite(v));
    return Array.from(new Set([-1, 0, ...dynamic]));
  }

  isSelectAllTagsChecked(): boolean {
    const allIds = this.getAllTagIds();
    if (!allIds.length) return false;
    const selected = (this.selectedTags || [])
      .map((v: any) => Number(v))
      .filter((v: number) => Number.isFinite(v) && v !== -2);
    return allIds.every(id => selected.includes(id)) && selected.length === allIds.length;
  }

  selectTag(tagId: number) {
    this.tagsChanged = true;

    // -2 used to be "Apply All". We now treat it as "Select All / Deselect All".
    if (tagId === -2) {
      this.selectedTags = this.isSelectAllTagsChecked() ? [] : this.getAllTagIds();
      return;
    }

    // Clean up any legacy -2 value if it exists
    this.selectedTags = (this.selectedTags || []).filter(id => id !== -2);
    let tagIndex = this.selectedTags.indexOf(tagId);
    if (tagIndex < 0) {
      this.selectedTags.push(tagId);
      switch(tagId) {
        case -1:
          tagIndex = this.selectedTags.indexOf(0);
          if (tagIndex >= 0)
            this.selectedTags.splice(tagIndex, 1)
          break;
        case 0:
          tagIndex = this.selectedTags.indexOf(-1);
          if (tagIndex >= 0)
            this.selectedTags.splice(tagIndex, 1)
          break;
      }
    }
    else
      this.selectedTags.splice(tagIndex, 1);
  }

  search(e: any) {
    for (let key of Object.keys(this.data.filters)) {
      if (this.data.filters[key].type == 'Contains')
        delete this.data.filters[key];
    }
    if (e.target.value) {
      this.data.filters['sku'] = { type: 'Contains', value: e.target.value.trim() };
      this.data.page = 1;
    }
    this.loadData();
  }

  loadConfigs(configs: AdvancedReorderConfig) {
    this.configs = configs;
    this.applyToAll = this.configs.applyToAll;
    if (this.configs.ruleId)
      this.advReorderRules.getAll(res => this.selectedRule = res.find(r => r.id == this.configs.ruleId));
    else {
      this.basedOn.selectedDays = this.configs.basedOnSelectedDays;
      this.basedOn.hasCustom = this.configs.basedOnHasCustom;
      this.basedOn.selectedMonths = this.configs.basedOnSelectedMonths;
      this.orderFor.selectedDays = this.configs.orderForSelectedDays;
      this.orderFor.hasCustom = this.configs.orderForHasCustom;
      this.orderFor.selectedMonths = this.configs.orderForSelectedMonths;
      this.adjSign = this.configs.adjustment > 0 ? '+' : '-';
      this.formGroup.controls.adjustment.setValue(this.configs.adjustment);
      this.formGroup.controls.outOfStock.setValue(this.configs.outOfStock);
      this.formGroup.controls.buyBox.setValue(this.configs.buyBox);
      this.formGroup.controls.minusInventory.setValue(false);
      this.formGroup.controls.minusInventory.setValue(this.configs.minusAllInventory);
      this.formGroup.controls.minusStoreInventory.setValue(false);
      this.formGroup.controls.minusStoreInventory.setValue(this.configs.minusAllStoresInventory);
      this.formGroup.controls.minusOverstockedStoreInventory.setValue(false);
      this.formGroup.controls.minusOverstockedStoreInventory.setValue(!!this.configs.minusAllStoresOverstockedInventory);
      for (let id of Object.keys(this.locationsFormGroup.value))
        this.locationsFormGroup.controls[id].setValue(this.configs.minusLocations.includes(id));
      for (let id of Object.keys(this.storesFormGroup.value))
        this.storesFormGroup.controls[id].setValue(this.configs.minusStores.includes(id));
      for (let id of Object.keys(this.overstockedStoresFormGroup.value))
        this.overstockedStoresFormGroup.controls[id].setValue(this.configs.minusOverstockedStores.includes(id));
      this.formGroup.controls.minusPOs.setValue(this.configs.minusPOs);
    }
  }

  applyConfigs() {
    this.configs.applyToAll = this.applyToAll;
    this.configs.ruleId = this.selectedRule?.id;
    if (!this.configs.ruleId) {
      this.configs.basedOnSelectedDays = this.basedOn.selectedDays;
      this.configs.basedOnHasCustom = this.basedOn.hasCustom;
      this.configs.basedOnSelectedMonths = this.basedOn.selectedMonths;
      this.configs.orderForSelectedDays = this.orderFor.selectedDays;
      this.configs.orderForHasCustom = this.orderFor.hasCustom;
      this.configs.orderForSelectedMonths = this.orderFor.selectedMonths;
      this.configs.adjustment = this.formGroup.value.adjustment;
      this.configs.outOfStock = this.formGroup.value.outOfStock;
      this.configs.buyBox = this.formGroup.value.buyBox;
      this.configs.minusAllInventory = this.minusAllInventory;
      this.configs.minusAllStoresInventory = this.minusAllStoresInventory;
      this.configs.minusAllStoresOverstockedInventory = this.minusAllStoresOverstockedInventory;
      this.configs.minusLocations = Object.keys(this.locationsFormGroup.value)
        .filter(id => this.locationsFormGroup.value[id] == true);
      this.configs.minusStores = Object.keys(this.storesFormGroup.value)
        .filter(id => this.storesFormGroup.value[id] == true);
      this.configs.minusOverstockedStores = Object.keys(this.overstockedStoresFormGroup.value)
        .filter(id => this.overstockedStoresFormGroup.value[id] == true);
      this.configs.minusPOs = this.formGroup.value.minusPOs;
    }
  }

  get showShadow() {
    return this.data?.items?.some(i => i.focus) || this.main?.focus;
  }

  loadData() { }

  loadItems(data: DatatableData) { }

  loadMain(data: DatatableData = this.data) {
    this.advReorderService.getById(this.data.parentKey, this.basedOn.selectedDays,
      this.basedOn.hasCustom ? `{${this.basedOn.getSelectedMonths().join(',')}}` : null, this.orderFor.selectedDays,
      this.orderFor.hasCustom ? `{${this.orderFor.getSelectedMonths().join(',')}}` : null, this.formGroup.value.adjustment,
      this.formGroup.value.outOfStock, this.formGroup.value.buyBox, this.minusAllInventory,
      this.locationsFormGroup ? Object.keys(this.locationsFormGroup.value).filter(id => this.locationsFormGroup.value[id] == true).map(id => Number(id)) : [],
      this.minusAllStoresInventory,
      this.storesFormGroup ? Object.keys(this.storesFormGroup.value).filter(id => this.storesFormGroup.value[id] == true).map(id => Number(id)) : [],
      this.minusAllStoresOverstockedInventory,
      this.overstockedStoresFormGroup ? Object.keys(this.overstockedStoresFormGroup.value).filter(id => this.overstockedStoresFormGroup.value[id] == true).map(id => Number(id)) : [],
      this.formGroup.value.minusPOs, !this.applyToAll).subscribe(result => {
        this.main = new AdvancedReorderItem();
        this.main.data = result || new AdvancedReorderModel();
        this.prepare(this.main);
        this.refreshOrders(this.main);
        this.loadMonthlyData(this.main);
        if (history.state.mainConfigs)
          this.resetItemConfigs(this.main, history.state.mainConfigs);
        else if (result.advancedReorderRuleId)
          this.selectRule(this.advReorderRules.rules.find(r => r.id == result.advancedReorderRuleId));
        this.loadItems(data);
      });
  }

  dataLoaded(result: AdvancedReordersModel) {
    this.data.parentKey = result.meta.parentKey;
    this.data.total = result.meta.total;
    this.data.page = result.meta.page;
    this.data.pageSize = result.meta.pageSize;
    this.data.sort = result.meta.sort;
    this.data.sortField = result.meta.sortField;
    this.loaded = result.data.length;
    this.itemsToLoad = [];
    this.allItems = this.data.items = [];
    for (let data of result.data)
      this.allItems.push(this.itemLoaded(data));
    this.loadNext();
    if (this.loaded == 0)
      this.loadingService.loading = false;
  }

  itemLoaded(data: AdvancedReorderModel) {
    let item = new AdvancedReorderItem();
    item.data = data || new AdvancedReorderModel();
    item.parentId = Number(this.data.parentKey);
    this.prepare(item);
    this.itemsToLoad.push(item);
    this.refreshOrders(item);
    return item;
  }

  getStore(id: number) {
    return this.stores.getAll().find((store) => store.id === id);
  }
  
  loadSupplierOptionsForAdvancedFilter() {
    this.suppliers.getAll(res => {
      const sortedSupplierNames = res.map(s => s.name).sort((a, b) => a.localeCompare(b));
      const first = sortedSupplierNames[0];
      const idx = this.filterOptions.findIndex(a => a.title === 'Supplier');
      this.filterOptions[idx].inputType = JSON.stringify(sortedSupplierNames);
      this.filterOptions[idx].value = first;
    });
  }
}